import { alpha, type Theme, type ThemeOptions } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

/** Theme slice for the design-system `Select` (styled to match `Input`). */
export function selectThemeComponents(theme: Theme): ThemeOptions['components'] {
  const selectedOptionBg = theme.palette.primary.light;
  const disabledFg = alpha('#1E2A3A', 0.4);
  const disabledChipBg = alpha('#F2F4F9', 0.7);

  return {
    MuiInputBase: {
      styleOverrides: {
        root: {
          // Custom `input` merges SmSelect-root onto the same node as MuiInputBase-root (not a wrapper).
          '&.SmSelect-root': {
            '& .MuiSelect-select': {
              flex: 1,
              minWidth: 0,
              paddingInlineEnd: 'calc(12px + 1.15rem + 8px)'
            },
            '& .SmSelect-value': {
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            },
            '&.SmSelect-multiple .MuiSelect-select': {
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              minHeight: '1.4375em',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            },
            '&.SmSelect-multiple .SmSelect-multipleChips': {
              display: 'flex',
              flexWrap: 'nowrap',
              overflow: 'hidden',
              gap: '4px',
              alignItems: 'center',
              minWidth: 0,
              maxWidth: '100%',
              flex: 1,
              minHeight: '1.4375em',
              '& .SmChip-root.MuiChip-sizeSmall': {
                height: 24,
                maxHeight: 24,
                flexShrink: 0,
                '& .MuiChip-label': {
                  paddingLeft: 8,
                  paddingRight: 8,
                  fontSize: '0.75rem',
                  lineHeight: 1.2
                }
              },
              '& .SmSelect-overflowChip': {
                flexShrink: 0
              }
            },
            '&.SmSelect-multiple .SmSelect-multipleChipsMeasure': {
              position: 'absolute',
              visibility: 'hidden',
              pointerEvents: 'none',
              height: 0,
              overflow: 'hidden',
              insetInlineStart: 0,
              insetBlockStart: 0,
              width: 'max-content'
            }
          },
          '&.SmSelect-root.MuiInputBase-adornedEnd': {
            position: 'relative',
            '& .MuiInputAdornment-positionEnd': {
              position: 'absolute',
              insetInlineEnd: 'calc(12px + 1.15rem + 4px)',
              top: '50%',
              transform: 'translateY(-50%)',
              margin: 0,
              maxHeight: 'none',
              height: 'auto'
            },
            '& .MuiSelect-select': {
              paddingInlineEnd: '52px'
            },
            '& .MuiInputAdornment-positionEnd .SmIconButton-root': {
              padding: 2
            }
          },
          '&.SmSelectChipInput-root': {
            'label + &': {
              marginTop: theme.spacing(2.5)
            },
            width: 'fit-content',
            borderRadius: '100px',
            overflow: 'hidden',
            '&.SmSelectChipInput-fullWidth': {
              width: '100%'
            },
            // The select display element lives under the input root.
            '& .MuiSelect-select': {
              borderRadius: '100px',
              paddingTop: '7px',
              paddingBottom: '7px',
              paddingRight: '44px !important',
              paddingLeft: '10px !important',
              backgroundColor: 'var(--sm-select-chip-bg, #F2F4F9)',
              color: 'var(--sm-select-chip-fg, #1E2A3A)',
              fontSize: 14,
              boxSizing: 'border-box',
              lineHeight: 1,
              minHeight: 'unset',
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              gap: '10px'
            },
            '&.Mui-focused .MuiSelect-select': {
              borderRadius: '100px'
            },
            '& .MuiSelect-icon': {
              right: '10px !important',
              left: 'auto',
              color: 'var(--sm-select-chip-fg, #1E2A3A) !important'
            },
            '&.Mui-disabled .MuiSelect-select': {
              backgroundColor: disabledChipBg,
              color: disabledFg,
              WebkitTextFillColor: disabledFg
            },
            '&.Mui-disabled .MuiSelect-icon': {
              color: `${disabledFg} !important`
            }
          }
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          '&.SmSelectMenu-paper': {
            backgroundColor: theme.palette.common.white,
            borderRadius: '10px',
            border: '1px solid var(--borders-core-10, #1E2A3A1A)',
            boxShadow: 'none'
          }
        },
        list: {
          '&.SmSelectMenu-list': {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(0.5),
            padding: theme.spacing(1),
            '& .MuiMenuItem-root': {
              borderRadius: 8,
              margin: 0,
              minHeight: 'auto',
              paddingTop: 3,
              paddingBottom: 3,
              '&.Mui-selected': {
                backgroundColor: selectedOptionBg
              },
              '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
                backgroundColor: selectedOptionBg
              }
            },
            '&.SmSelectMenu-list--single .MuiMenuItem-root': {
              paddingTop: 10,
              paddingBottom: 10
            },
            '&.SmSelectMenu-list--multiple .MuiMenuItem-root': {
              paddingTop: 0,
              paddingBottom: 0,
              paddingInline: 12
            },
            '& .SmSelectMenu-item': {
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1),
              width: '100%',
              minWidth: 0
            },
            '& .SmSelectMenu-itemCheckbox': {
              pointerEvents: 'none',
              display: 'inline-flex',
              flexShrink: 0
            },
            '& .SmSelectMenu-itemLabel': {
              flex: 1,
              minWidth: 0
            },
            '& .SmSelectMenu-divider': {
              marginTop: theme.spacing(0.5),
              marginBottom: theme.spacing(0.5)
            }
          }
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDownIcon
      },
      styleOverrides: {
        root: {
          '& ul': {
            direction: 'rtl !important'
          },
          '&.SmSelect-appearance-plain .MuiSelect-icon': {
            color: '#1E2A3A !important'
          },
          '&.SmSelect-appearance-plain.Mui-disabled .MuiSelect-icon': {
            color: 'rgba(30, 42, 58, 0.4) !important'
          }
        },
        icon: {
          right: '15px !important',
          fontSize: '1.15rem !important',
          width: '1.15rem !important',
          height: '1.15rem !important'
        }
      }
    }
  };
}
